import { ApiClientReturnType, Nullable } from '~/types';
import api from '../http-common';
import { CoolFarmCropOptionsType } from '~/pages/Farmer/CoolFarm/AnswerCrop/types';

const getCoolFarmCropAnsOptions = (id: string): ApiClientReturnType<CoolFarmCropOptionsType> =>
  api.post(`/cool-farm-tool/footprints/${id}/answer/crops/get-options-data`)

const saveCoolFarmCropAnswers = (id: string, data) =>
  api.post(`/cool-farm-tool/footprints/${id}/answer/crops/save`, data)

const editCoolFarmCropAnswers = (id: string, idCrop: string): ApiClientReturnType<CropEditType> =>
  api.post(`/cool-farm-tool/footprints/${id}/answer/crops/${idCrop}/edit`)

const checkCoolFarmCropAnswers = (id: string, data) =>
  api.post(`/cool-farm-tool/footprints/${id}/answer/crops/check`, data)

const storeCoolFarmCropAnswers = (id: string, idCrop: string, data) =>
  api.post(`/cool-farm-tool/footprints/${id}/answer/crops/${idCrop}/store`, data)

const updateCoolFarmCropAnswers = (id: string, idCrop: string, data) =>
  api.post(`/cool-farm-tool/footprints/${id}/answer/crops/${idCrop}/update`, data)

const getCrops = (id: string, data) =>
  api.post(`/cool-farm-tool/footprints/${id}/answer/crops/index`, data)

const getCropResults = (id: string, idCrop: string): ApiClientReturnType<CoolFarmCropResultsType> =>
  api.post(`/cool-farm-tool/footprints/${id}/answer/crops/${idCrop}/get-results`)

export const CoolFarmCropService = {
  getCoolFarmCropAnsOptions,
  saveCoolFarmCropAnswers,
  storeCoolFarmCropAnswers,
  editCoolFarmCropAnswers,
  checkCoolFarmCropAnswers,
  updateCoolFarmCropAnswers,
  getCrops,
  getCropResults
}

export interface CoProduct {
  id: number;
  cftFootprintAnswerId: number;
  name: string;
  percent: string;
}

export interface Fertilizer {
  id: number;
  emissionsInhibitors: number;
  cftIngredientId: number;
  amount: string;
  amountUnit: string;
  cftProductionRegionId: number;
  applicationDate: string;
  applicationMethod: number;
}

export interface Protection {
  category: number;
  type: number;
  applicationRate: number;
  applicationRateUnit: number;
  percentage: number;
}

export interface EnergyType {
  id: number;
  amount: number;
  amountUnit: number;
  category: number;
}

export interface FieldOperation {
  id: number;
  fuelType: number;
  numberOfOperations: number;
}

export interface IrrigationEvent {
  method: number;
  waterSource: number;
  depth: number;
  depthUnit: number;
  distance: number;
  distanceUnit: number;
  powerSource: number;
  addedWater: number;
  addedWaterUnit: number;
  irrigatedArea: number;
}

export interface CarbonChange {
  year: number;
  percentage: number;
  landUseFrom: number;
  landUseTo: number;
  tillageTypeFrom: number;
  tillageTypeTo: number;
  carbonInputFrom: number;
  carbonInputTo: number;
}

export interface TreeType {
  id: number;
  densityLastYear: number;
  sizeLastYear: number;
  sizeThisYear: number;
  treeBalance: number;
}

export interface TransportType {
  id: number;
  weight: string;
  weightUnit: number;
  distance: string;
}

export interface CropEditType {
  name: string;
  cftCropTypeId: number;
  harvestingYear: number;
  cropArea: number;
  freshAmount: number;
  dryAmount: number;
  amountUnit: number;
  step: number;
  hasTreeBiomass: Nullable<number>;

  residueManagementType: number;
  residueAmount: number;
  residueAmountUnit: number;

  hasCoProducts: number;

  soilTexture: number;
  soilOrganicMatterValue: number;
  soilDrainageType: number;
  soilPhValue: number;

  coProducts: CoProduct[];
  fertilizers: Fertilizer[];
  protections: Protection[];
  energyTypes: EnergyType[];
  fieldOperations: FieldOperation[];
  irrigationEvents: IrrigationEvent[];
  carbonChanges: CarbonChange[];
  treeTypes: TreeType[];
  transportTypes: TransportType[];
}

export interface CoolFarmCropResultsType {
  // Summary values
  totalEmission: string;
  emissionPerArea: string;
  emissionPerProduct: string;
  
  // Total emissions by gas type
  totalCo2Emission: string;
  totalCo2EmissionInCo2e: string;
  totalN2oEmission: string;
  totalN2oEmissionInCo2e: string;
  totalCh4Emission: string;
  totalCh4EmissionInCo2e: string;
  
  // Co-product emissions
  coProductCo2Emission: string;
  coProductN2oEmission: string;
  coProductCh4Emission: string;
  coProductTotalEmission: string;
  coProductTotalEmissionPerArea: string;
  coProductTotalEmissionPerProduct: string;
  
  // Fertiliser production emissions
  fertiliserProductionCo2Emission: string;
  fertiliserProductionN2oEmission: string;
  fertiliserProductionCh4Emission: string;
  fertiliserProductionTotalEmission: string;
  fertiliserProductionTotalEmissionPerArea: string;
  fertiliserProductionTotalEmissionPerProduct: string;
  
  // Fertiliser soil emissions
  fertiliserSoilCo2Emission: string;
  fertiliserSoilN2oEmission: string;
  fertiliserSoilCh4Emission: string;
  fertiliserSoilTotalEmission: string;
  fertiliserSoilTotalEmissionPerArea: string;
  fertiliserSoilTotalEmissionPerProduct: string;
  
  // Field energy emissions
  fieldEnergyCo2Emission: string;
  fieldEnergyN2oEmission: string;
  fieldEnergyCh4Emission: string;
  fieldEnergyTotalEmission: string;
  fieldEnergyTotalEmissionPerArea: string;
  fieldEnergyTotalEmissionPerProduct: string;
  
  // Irrigation emissions
  irrigationCo2Emission: string;
  irrigationN2oEmission: string;
  irrigationCh4Emission: string;
  irrigationTotalEmission: string;
  irrigationTotalEmissionPerArea: string;
  irrigationTotalEmissionPerProduct: string;
  
  // Machinery emissions
  machineryCo2Emission: string;
  machineryN2oEmission: string;
  machineryCh4Emission: string;
  machineryTotalEmission: string;
  machineryTotalEmissionPerArea: string;
  machineryTotalEmissionPerProduct: string;
  
  // Management changes emissions
  managementChangesCo2Emission: string;
  managementChangesN2oEmission: string;
  managementChangesCh4Emission: string;
  managementChangesTotalEmission: string;
  managementChangesTotalEmissionPerArea: string;
  managementChangesTotalEmissionPerProduct: string;
  
  // Pesticide emissions
  pesticideCo2Emission: string;
  pesticideN2oEmission: string;
  pesticideCh4Emission: string;
  pesticideTotalEmission: string;
  pesticideTotalEmissionPerArea: string;
  pesticideTotalEmissionPerProduct: string;
  
  // Residue emissions
  residueCo2Emission: string;
  residueN2oEmission: string;
  residueCh4Emission: string;
  residueTotalEmission: string;
  residueTotalEmissionPerArea: string;
  residueTotalEmissionPerProduct: string;
  
  // Transportation emissions
  transportationCo2Emission: string;
  transportationN2oEmission: string;
  transportationCh4Emission: string;
  transportationTotalEmission: string;
  transportationTotalEmissionPerArea: string;
  transportationTotalEmissionPerProduct: string;
  
  // Waste water emissions
  wasteWaterCo2Emission: string;
  wasteWaterN2oEmission: string;
  wasteWaterCh4Emission: string;
  wasteWaterTotalEmission: string;
  wasteWaterTotalEmissionPerArea: string;
  wasteWaterTotalEmissionPerProduct: string;
}
